import { createHook, createStore, StoreActionApi } from "react-sweet-state"
import { load, LoadingState, LoadingStateDefaults } from "components/state/loader"
import { LoadingStateModifiers } from "components/state/loadingstate"
import { EndpointResponse } from "components/http/endpoint"
import { StatusCode } from "components/http/ajax/statusCode"
import { Container } from "components/state/types"
import { MemberValidationState, MemberValidation, MemberValidationDefaults } from "./entity"
import { NoEntityError, updateByResponse, updateErrorsFromResponse, errorToEntityError } from "components/validation/validatableentity"
import {
    apiSendEmailValidationRequest,
    apiSendPhoneValidationRequest,
    apiGetValidationState,
    apiValidateEmail,
    apiValidatePhone,

} from "./endpoint";
import { EntityState, setEntityField, setStateField } from "components/state/entity"



interface State extends EntityState<MemberValidation> {
    dataValid: boolean;
    lastChangeTime: Date;
    showPhoneVerificationCodeDialog: boolean;
    emailValidationRequestSucceeded: boolean;
    phoneValidationRequestSucceeded: boolean;
}

const StateDefaults: State = {
    ...LoadingStateDefaults,
    entity: MemberValidationDefaults,
    errors: { ...NoEntityError },
    dataValid: true,
    lastChangeTime: new Date,
    showPhoneVerificationCodeDialog: false,
    emailValidationRequestSucceeded: false,
    phoneValidationRequestSucceeded: false,
}


const isMemberValidationState = (state: MemberValidationState, stateFlag: MemberValidationState): boolean => {
    return (state & stateFlag) !== 0;
}


const sendEmailValidationRequest = (memberIdt: number, email: string) =>
    (store: StoreActionApi<State>) => {

        if (!memberIdt) {
            return;
        }

        store.setState(draft => {
            LoadingStateModifiers.startLoading(draft);
            draft.emailValidationRequestSucceeded = false;
        });

        const resolve = (response: EndpointResponse) => {

            store.setState(draft => {
                LoadingStateModifiers.stopLoading(draft, response.succeeded);

                // co v pripade response.succeeded==false? stav nechat nebo nastavit na Invalid?
                // nebo vzdy volat getValidationState ?
                if (response.succeeded) {
                    draft.emailValidationRequestSucceeded = true;
                    draft.dataValid = false;
                    draft.lastChangeTime = new Date;
                } else {
                    updateErrorsFromResponse(draft, response);
                }
            });

        }

        let loader = () => apiSendEmailValidationRequest(memberIdt, email);

        const promise = loader();

        return promise.then(
            (success: EndpointResponse) => resolve(success),
            (failure: EndpointResponse) => resolve(failure)
        );
    };

const sendPhoneValidationRequest = (memberIdt: number, phone: string) =>
    (store: StoreActionApi<State>) => {

        if (!memberIdt) {
            return;
        }

        store.setState(draft => {
            LoadingStateModifiers.startLoading(draft);
            draft.phoneValidationRequestSucceeded = false;
        });

        const resolve = (response: EndpointResponse) => {

            store.setState(draft => {
                LoadingStateModifiers.stopLoading(draft, response.succeeded);

                // co v pripade response.succeeded==false? stav nechat nebo nastavit na Invalid?
                // nebo vzdy volat getValidationState ?
                if (response.succeeded) {
                    draft.phoneValidationRequestSucceeded = true;
                    draft.dataValid = false;
                    draft.lastChangeTime = new Date;
                } else {
                    updateErrorsFromResponse(draft, response);
                }
            });
        }

        let loader = () => apiSendPhoneValidationRequest(memberIdt, phone);

        const promise = loader();

        return promise.then(
            (success: EndpointResponse) => resolve(success),
            (failure: EndpointResponse) => resolve(failure)
        );
    };

const getValidationState = (memberIdt: number) =>
    (store: StoreActionApi<State>) => {

        if (!memberIdt || store.getState().dataValid) {
            return;
        }

        let loader = () => apiGetValidationState(memberIdt);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            updateByResponse(draft, response);
            // co v pripade response.succeeded==false? stav nechat nebo nastavit na Invalid?
            if (response.succeeded) {
                draft.dataValid = true;
                draft.lastChangeTime = new Date;
            }
        }

        load<State>(store, loader, stateProducer);
    };


const invalidateValidationCache = () =>
    (store: StoreActionApi<State>) => {
        store.setState(state => {
            state.dataValid = false;
            state.lastChangeTime = new Date;
        });
    };

const validateEmail = (hashCode: string) =>
    (store: StoreActionApi<State>) => {

        let loader = () => apiValidateEmail(hashCode);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            // co v pripade response.succeeded==false? stav nechat nebo nastavit na Invalid?
            if (response.succeeded) {
                updateByResponse(draft, response);
                draft.dataValid = true;
                draft.lastChangeTime = new Date;
            } else {
                const error = response.status == StatusCode.NotFound
                    ? "Ověřovací kód jsme nenašli. Pravděpodobně je pomocí tohoto kódu již email ověřen."
                    : "Při ověřování došlo k chybě. Zkuste provést znovu později.";
                draft.errors[""] = { errors: error }
            }
        }

        load<State>(store, loader, stateProducer);
    };



const validatePhone = (memberIdt: number) =>
    (store: StoreActionApi<State>) => {

        const { entity } = store.getState();

        let loader = () => apiValidatePhone(memberIdt, entity.code);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            updateByResponse(draft, response);
            // co v pripade response.succeeded==false? stav nechat nebo nastavit na Invalid?
            if (response.succeeded) {
                draft.dataValid = true;
                draft.lastChangeTime = new Date;
                draft.showPhoneVerificationCodeDialog = false;
            } else {
                if (response.status == StatusCode.NotFound) {
                    errorToEntityError(draft, 'Kód není platný, nebo vypršel čas pro ověření.', true, "code");
                }
            }
        }

        load<State>(store, loader, stateProducer);
    };

const showPhoneVerificationCodeDialog = (show: boolean) =>
    (store: StoreActionApi<State>) => {
        store.setState(draft => {
            draft.showPhoneVerificationCodeDialog = show;
        });
    };

const store = createStore({
    initialState: StateDefaults,
    actions: {
        getValidationState,
        sendEmailValidationRequest,
        sendPhoneValidationRequest,
        invalidateValidationCache,
        showPhoneVerificationCodeDialog,
        validateEmail,
        validatePhone,
        setEntityField,
    },
    name: "MemberValidation"
});

const useState = createHook(store);

export {
    StateDefaults as MemberValidationStateDefaults,
    State as MemberValidationState,
    useState as useMemberValidation,
    isMemberValidationState,
}
